"use client"

import { create } from "zustand"

import { clearActiveWorkspacePersist } from "@/features/auth/services/auth-context-store"

import { Currency } from "@ibada/shared-types/finance"

export type BranchSummary = {
  id: string
  name: string
  slug: string
  branchType?: "NORMAL" | "SYSTEM"
  /**
   * The branch's ACCOUNTING currency (IBA-114). Accounting belongs to branches, so every finance figure scoped
   * to this branch is denominated in this — never in a workspace-wide setting. It rides along with the branch
   * summary so no finance surface needs a second fetch to know what currency it is showing.
   */
  baseCurrency?: Currency
  /** IANA timezone that defines this branch's calendar and document dates. */
  timezone?: string
}

export type UserBranchAccess = {
  primaryBranch: BranchSummary | null
  accessibleBranches: BranchSummary[]
}

const DEFAULT_BRANCH_ACCESS: UserBranchAccess = {
  primaryBranch: null,
  accessibleBranches: [],
}

type UserBranchAccessState = UserBranchAccess & {
  hydrated: boolean
  setBranchAccess: (input: UserBranchAccess) => void
  hydrate: () => void
}

/** Clear transient derived branch state on sign-out or account change. */
export function clearUserBranchAccessPersist() {
  useUserBranchAccessStore.getState().setBranchAccess({
    primaryBranch: DEFAULT_BRANCH_ACCESS.primaryBranch,
    accessibleBranches: DEFAULT_BRANCH_ACCESS.accessibleBranches,
  })
  clearActiveWorkspacePersist()
}

export const useUserBranchAccessStore = create<UserBranchAccessState>()((set) => ({
  hydrated: false,
  primaryBranch: DEFAULT_BRANCH_ACCESS.primaryBranch,
  accessibleBranches: DEFAULT_BRANCH_ACCESS.accessibleBranches,
  setBranchAccess: (input) =>
    set({
      primaryBranch: input.primaryBranch,
      accessibleBranches: input.accessibleBranches,
    }),
  hydrate: () => set({ hydrated: true }),
}))
